import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import { toast } from "react-toastify";
import {
  postComparisonRule,
  postValidationRule,
  postCausalityRule,
  postHistoricalRule,
} from "../../Services/RuleServices";
import {
  ComparisonTypeEnum,
  RuleStateEnum,
  RuleTypeEnum,
  ValidationRuleTypeEnum,
} from "../../Enum";
import AllRulesForm from "../RuleForm/RuleForm";
import {
  CreateCausalityRuleRequest,
  CreateComparisonRuleRequest,
  CreateHistoricalRuleRequest,
  CreateValidationRuleRequest,
  manResponseType,
} from "../../interfaces";

const RuleForm = () => {
  const [ruleType, setRuleType] = useState<RuleTypeEnum>(RuleTypeEnum.Comparison);
  const [responseDescriptions, setResponseDescriptions] = useState<manResponseType[]>([]);
  const [idManResponseAvailable, setIdManResponseAvailable] = useState<number[]>([]);
  const [manifestNames, setManifestNames] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Load response descriptions
  useEffect(() => {
    const fetchResponseDescription = async () => {
      try {
        const response = await fetch("/response_description.csv");
        if (!response.ok) {
          throw new Error("Failed to fetch response descriptions");
        }
        
        const csvText = await response.text();
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (result: any) => {
            const parsed: manResponseType[] = result.data
              .filter(
                (row: any) =>
                  row.Id &&
                  row.id &&
                  row.description_es &&
                  row.description_es !== "#ERROR!"
              )
              .map((row: any) => ({
                Id: Number(row.Id),
                Description: row.Description,
                ResponseType: row.ResponseType,
                id: Number(row.id),
                description_es: row.description_es,
              }));
            
            setResponseDescriptions(parsed);
            setIdManResponseAvailable(
              Array.from(new Set(parsed.map((item) => item.Id)))
            );
          },
        });
      } catch (error) {
        console.error("Error loading response_description.csv:", error);
        toast.error("Failed to load response descriptions");
      }
    };

    fetchResponseDescription();
  }, []);

  // Load manifestation names
  useEffect(() => {
    const fetchManifestNames = async () => {
      try {
        const response = await fetch("/nombre_manifestaciones.csv");
        if (!response.ok) {
          throw new Error("Failed to fetch manifestation names");
        }
        
        const csvText = await response.text();
        Papa.parse(csvText, {
          header: false,
          skipEmptyLines: true,
          complete: (result: any) => {
            const names: Record<string, string> = {};
            result.data.forEach((row: string[]) => {
              if (row[0] && row[1]) {
                names[row[1]] = row[0];
              }
            });
            setManifestNames(names);
            setIsLoadingData(false);
          },
        });
      } catch (error) {
        console.error("Error loading manifestation names:", error);
        toast.error("Failed to load manifestation names");
        setIsLoadingData(false);
      }
    };

    fetchManifestNames();
  }, []);

  const wrapSubmit = async (fn: () => Promise<void>) => {
    setIsSubmitting(true);
    try {
      await fn();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComparisonSubmit = async (data: any) =>
    wrapSubmit(async () => {
      const request: CreateComparisonRuleRequest = {
        firstVariableName: data.firstVariableName ?? "",
        secondVariableName: data.secondVariableName ?? "",
        comparisonType: data.comparisonType ?? ComparisonTypeEnum.EqualTo,
        errorMessage: data.errorMessage ?? "",
        ruleState: RuleStateEnum.Active,
        applicablePhaseIds: data.applicablePhaseIds ?? [],
        type: "ComparisonRule",
      };

      try {
        await postComparisonRule(request);
        toast.success("Comparison rule added successfully");
      } catch (error) {
        // Error already handled by service
        console.error("Failed to add Comparison rule:", error);
      }
    });

  const handleValidationSubmit = async (data: any) =>
    wrapSubmit(async () => {
      // If ruleType is Required, value should be "0"
      if (data.ruleType === ValidationRuleTypeEnum.Required) {
        data.value = "0";
      }
      
      const request: CreateValidationRuleRequest = {
        variableName: data.variableName ?? "",
        ruleType: data.ruleType ?? 0,
        value: data.value ?? "",
        errorMessage: data.errorMessage ?? "",
        ruleState: RuleStateEnum.Active,
        applicablePhaseIds: data.applicablePhaseIds ?? [],
        type: "ValidationRule",
      };

      try {
        await postValidationRule(request);
        toast.success("Validation rule added successfully");
      } catch (error) {
        // Error already handled by service
        console.error("Failed to add Validation rule:", error);
      }
    });

  const handleCausalitySubmit = async (data: any) =>
    wrapSubmit(async () => {
      const request: CreateCausalityRuleRequest = {
        firstVariableName: data.firstVariableName ?? "",
        firstCondition: data.firstCondition ?? ComparisonTypeEnum.EqualTo,
        firstValue: data.firstValue ?? "",
        secondVariableName: data.secondVariableName ?? "",
        secondCondition: data.secondCondition ?? ComparisonTypeEnum.EqualTo,
        secondValue: data.useFirstVariableValue
          ? undefined
          : data.secondValue ?? "",
        useFirstVariableValue: data.useFirstVariableValue ?? false,
        errorMessage: data.errorMessage ?? "",
        ruleState: RuleStateEnum.Active,
        applicablePhaseIds: data.applicablePhaseIds ?? [],
        type: "CausalityRule",
      };

      try {
        await postCausalityRule(request);
        toast.success("Causality rule added successfully");
      } catch (error) {
        // Error already handled by service
        console.error("Failed to add Causality rule:", error);
      }
    });

  const handleHistoricalSubmit = async (data: any) =>
    wrapSubmit(async () => {
      const request: CreateHistoricalRuleRequest = {
        variableName: data.variableName ?? "",
        relatedVariablesPrefix: data.relatedVariablesPrefix ?? "1",
        relatedCondition: data.relatedCondition ?? ComparisonTypeEnum.EqualTo,
        relatedValue: data.relatedValue ?? "",
        errorMessage: data.errorMessage ?? "",
        ruleState: RuleStateEnum.Active,
        applicablePhaseIds: data.applicablePhaseIds ?? [],
        type: "HistoricalRule",
      };

      try {
        await postHistoricalRule(request);
        toast.success("Historical rule added successfully");
      } catch (error) {
        // Error already handled by service
        console.error("Failed to add Historical rule:", error);
      }
    });

  // Show loading state while fetching CSV data
  if (isLoadingData) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
        <p className="text-gray-600">Loading manifestation data...</p>
      </div>
    );
  }

  // Show loading state while submitting
  if (isSubmitting) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
        <p className="text-gray-600">Submitting rule...</p>
      </div>
    );
  }

  return (
    <AllRulesForm
      ruleType={ruleType}
      setRuleType={setRuleType}
      onComparisonSubmit={handleComparisonSubmit}
      onValidationSubmit={handleValidationSubmit}
      onCausalitySubmit={handleCausalitySubmit}
      onHistoricalSubmit={handleHistoricalSubmit}
      manifestNames={manifestNames}
      responseDescriptions={responseDescriptions}
      idManResponseAvailable={idManResponseAvailable}
    />
  );
};

export default RuleForm;